import { useState } from "react";
import { enqueueSnackbar } from "notistack";

import { supabase } from "@/shared/lib/supabase";
import { TOAST } from "@/shared/constants/toastMessages";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { EventType, trackEvent } from "@/shared/utils/core";
import { sentryUtils } from "@/shared/utils/sentry";
import { toErrorType } from "@/shared/utils/funnelAnalytics";
import type { PromptLocation } from "@/shared/utils/funnelAnalytics";
import type { PaymentTier } from "@/shared/utils/tierOrder";

const CHECKOUT_CONTEXT_ANALYTICS: Record<string, string> = {
  POST_LOSS_PAYWALL: "post_loss_paywall",
  ASSET_SEARCH_CHECKOUT: "find_assets",
  POST_LOSS_ONBOARDING: "onboarding_access_code",
  DOCUMENT_LIMIT_PAYWALL: "document_limit_paywall",
  ASSET_SEARCH_UPSELL: "asset_search_upsell",
  client_invite: "client_invite",
};

interface UseCheckoutOptions {
  returnUrl: string;
  cancelUrl: string;
  context: string;
  promptLocation?: PromptLocation;
  fromTier?: PaymentTier | null;
}

export function useCheckout({
  returnUrl,
  cancelUrl,
  context,
  promptLocation,
  fromTier = "free",
}: UseCheckoutOptions) {
  const { user, person } = useAppContext();
  const [loadingTier, setLoadingTier] = useState<PaymentTier | null>(null);

  const isLoading = loadingTier !== null;

  const startCheckout = async (toTier: PaymentTier, promotionCode?: string) => {
    if (isLoading) return;

    setLoadingTier(toTier);
    const checkoutContext = CHECKOUT_CONTEXT_ANALYTICS[context] ?? context.toLowerCase();
    const trackCheckoutFailed = (err: unknown) =>
      trackEvent(EventType.CHECKOUT_FAILED, {
        checkout_context: checkoutContext,
        prompt_location: promptLocation,
        plan_type: toTier,
        error_type: toErrorType(err),
      });

    try {
      if (!user?.user_id) {
        setLoadingTier(null);
        return;
      }

      const { data, error } = await supabase.functions.invoke("billing/checkout", {
        body: {
          returnUrl,
          cancelUrl,
          userId: user.user_id,
          estateId: person?.estate_id,
          context,
          promptLocation,
          sourcePath: window.location.pathname,
          createCustomer: true,
          mode: "payment",
          fromTier: fromTier || "free",
          toTier,
          ...(promotionCode ? { promotionCode } : {}),
        },
      });

      if (error) {
        trackCheckoutFailed(error);
        enqueueSnackbar(TOAST.GENERIC_ERROR, { variant: "error" });
        sentryUtils.captureError(error, `Checkout session error (${context})`);
        setLoadingTier(null);
        return;
      }

      if (data?.url) {
        trackEvent(EventType.CHECKOUT_STARTED, {
          checkout_context: checkoutContext,
          prompt_location: promptLocation,
          plan_type: toTier,
          has_promotion_code: !!promotionCode,
        });
        window.location.href = data.url;
      } else {
        trackCheckoutFailed(null);
        enqueueSnackbar(TOAST.GENERIC_ERROR, { variant: "error" });
        setLoadingTier(null);
      }
    } catch (error) {
      trackCheckoutFailed(error);
      enqueueSnackbar("Something went wrong. Please try again.", { variant: "error" });
      sentryUtils.captureError(error, `Checkout error (${context})`, {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
      setLoadingTier(null);
    }
  };

  return { startCheckout, loadingTier, isLoading };
}
